export interface CameraCapabilities {
  torch: boolean;
  zoom: { min: number; max: number; step: number } | null;
}

/** Torch/zoom live in vendor extensions the DOM types don't know about. */
type ExtendedCapabilities = {
  torch?: boolean;
  zoom?: { min?: number; max?: number; step?: number };
};

export function getTrackCapabilities(track: MediaStreamTrack): CameraCapabilities {
  let raw: ExtendedCapabilities = {};
  try {
    if (typeof track.getCapabilities === 'function') {
      raw = track.getCapabilities() as ExtendedCapabilities;
    }
  } catch {
    // Some implementations throw on an ended track; "no capabilities" is the
    // honest answer, and this accessor is documented never to throw.
  }
  const zoom = raw.zoom;
  return {
    torch: raw.torch === true,
    zoom:
      zoom && typeof zoom.min === 'number' && typeof zoom.max === 'number'
        ? { min: zoom.min, max: zoom.max, step: zoom.step ?? 0.1 }
        : null,
  };
}

/**
 * Applies one advanced constraint, reporting success as a boolean. Rejection
 * counts as "not applied", never as a throw: plenty of devices advertise a
 * capability and then refuse the constraint (`OverconstrainedError`,
 * `NotSupportedError`), and an ended track refuses everything.
 */
async function applyAdvanced(
  track: MediaStreamTrack,
  constraint: MediaTrackConstraintSet,
): Promise<boolean> {
  try {
    await track.applyConstraints({ advanced: [constraint] });
    return true;
  } catch {
    return false;
  }
}

/**
 * Turns the torch on/off. Resolves to false (without throwing) when the
 * device has no controllable torch — or advertises one it will not actually
 * apply — so callers can no-op gracefully.
 */
export async function applyTorch(track: MediaStreamTrack, on: boolean): Promise<boolean> {
  if (!getTrackCapabilities(track).torch) return false;
  return applyAdvanced(track, { torch: on } as MediaTrackConstraintSet);
}

/** Sets zoom, clamped to the supported range. False when unsupported. */
export async function applyZoom(track: MediaStreamTrack, zoom: number): Promise<boolean> {
  const range = getTrackCapabilities(track).zoom;
  if (range === null) return false;
  const clamped = Math.min(range.max, Math.max(range.min, zoom));
  return applyAdvanced(track, { zoom: clamped } as MediaTrackConstraintSet);
}
